import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, ExternalLink } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { ImageUpload, inputCls } from "@/admin/fields";
import { Switch } from "@/components/ui/switch";

const FieldText = ({ label, value, onChange, testid, textarea, rows = 3 }) => (
  <div>
    <label className="mb-1 block text-xs font-bold uppercase text-slate-400">{label}</label>
    {textarea
      ? <textarea rows={rows} className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
      : <input className={inputCls} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />}
  </div>
);

const ToggleRow = ({ label, desc, checked, onChange, testid }) => (
  <div className="flex items-center justify-between rounded-lg border border-slate-700 p-4">
    <div><div className="text-sm font-bold uppercase text-slate-200">{label}</div>{desc && <div className="text-xs text-slate-400">{desc}</div>}</div>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

function ListEditor({ label, items, onChange, fields, prefix }) {
  const update = (i, k, v) => { const next = [...items]; next[i] = { ...next[i], [k]: v }; onChange(next); };
  const add = () => onChange([...(items || []), fields.reduce((a, f) => ({ ...a, [f.key]: "" }), {})]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <label className="text-xs font-bold uppercase text-slate-400">{label}</label>
        <button onClick={add} className="inline-flex items-center gap-1 rounded-md bg-[#1E63D6] px-3 py-1.5 text-xs font-bold text-white" data-testid={`${prefix}-add`}><Plus className="h-3.5 w-3.5" /> Add</button>
      </div>
      <div className="space-y-4">
        {(items || []).map((it, i) => (
          <div key={i} className="rounded-lg border border-slate-700 bg-[#0F1826] p-4" data-testid={`${prefix}-item-${i}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-bold uppercase text-slate-500">#{i + 1}</span>
              <button onClick={() => remove(i)} className="text-red-400 hover:text-red-300" data-testid={`${prefix}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="grid gap-3">
              {fields.map((f) => (
                f.type === "image"
                  ? <div key={f.key}><label className="mb-1 block text-xs font-bold uppercase text-slate-400">{f.label}</label><ImageUpload value={it[f.key]} onChange={(v) => update(i, f.key, v)} testid={`${prefix}-${i}-${f.key}`} /></div>
                  : <FieldText key={f.key} label={f.label} value={it[f.key]} onChange={(v) => update(i, f.key, v)} textarea={f.type === "textarea"} rows={2} testid={`${prefix}-${i}-${f.key}`} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlatformCard({ pageKey, title, children, c, setC }) {
  const set = (k, v) => setC({ ...c, [k]: v });
  return (
    <div className="rounded-xl border border-slate-700 bg-[#151F30] p-5">
      <div className="mb-4 flex items-center gap-3">
        <h2 className="font-heading text-lg font-extrabold uppercase text-white">{title}</h2>
        {c.external_url && <a href={c.external_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#2E7DF6]"><ExternalLink className="h-3.5 w-3.5" /> Visit</a>}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <ToggleRow label="Page visible" desc="Show the dedicated page" checked={c.visible} onChange={(v) => set("visible", v)} testid={`${pageKey}-visible`} />
        <ToggleRow label="Show on homepage" desc="Display the homepage section" checked={c.show_on_home} onChange={(v) => set("show_on_home", v)} testid={`${pageKey}-show-home`} />
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

export default function PlatformsManager() {
  const [rn, setRn] = useState(null);
  const [ug, setUg] = useState(null);
  const [ps, setPs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pages/rugby_nexus").then((r) => setRn(r.data?.content || {}));
    api.get("/admin/pages/untamed_gear").then((r) => setUg(r.data?.content || {}));
    api.get("/admin/pages/partners").then((r) => setPs(r.data?.content || {}));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pages/rugby_nexus", { content: rn });
      await api.put("/admin/pages/untamed_gear", { content: ug });
      await api.put("/admin/pages/partners", { content: ps });
      toast.success("Platforms & suppliers saved");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!rn || !ug || !ps) return <Loader2 className="mx-auto mt-20 h-7 w-7 animate-spin text-slate-400" />;
  const setRnK = (k, v) => setRn({ ...rn, [k]: v });
  const setUgK = (k, v) => setUg({ ...ug, [k]: v });
  const setPsItem = (i, k, v) => { const items = [...(ps.items || [])]; items[i] = { ...items[i], [k]: v }; setPs({ ...ps, items }); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-extrabold uppercase text-white">Platforms & Suppliers</h1><p className="text-sm text-slate-400">Manage the Rugby Nexus and Untamed Sports Gear pages and homepage sections.</p></div>
        <button onClick={save} disabled={saving} data-testid="platforms-save-button" className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-5 py-2 text-sm font-bold uppercase text-[#0A1626]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
      </div>

      <div className="mt-6 space-y-8">
        {/* RUGBY NEXUS */}
        <PlatformCard pageKey="rugby-nexus" title="Rugby Nexus" c={rn} setC={setRn}>
          <FieldText label="Page Heading" value={rn.heading} onChange={(v) => setRnK("heading", v)} testid="rn-heading" />
          <FieldText label="Tagline" value={rn.tagline} onChange={(v) => setRnK("tagline", v)} testid="rn-tagline" />
          <div className="sm:col-span-2"><FieldText label="Introduction" value={rn.intro} onChange={(v) => setRnK("intro", v)} textarea rows={4} testid="rn-intro" /></div>
          <FieldText label="Button Text" value={rn.button_text} onChange={(v) => setRnK("button_text", v)} testid="rn-button" />
          <FieldText label="External Website URL" value={rn.external_url} onChange={(v) => setRnK("external_url", v)} testid="rn-url" />
          <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Logo</label><ImageUpload value={rn.logo_url} onChange={(v) => setRnK("logo_url", v)} testid="rn-logo" /></div>
          <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Featured Image</label><ImageUpload value={rn.featured_image} onChange={(v) => setRnK("featured_image", v)} testid="rn-featured" /></div>
          <FieldText label="Approach Title" value={rn.approach_title} onChange={(v) => setRnK("approach_title", v)} testid="rn-approach-title" />
          <div className="sm:col-span-2"><FieldText label="Approach Text" value={rn.approach_text} onChange={(v) => setRnK("approach_text", v)} textarea rows={4} testid="rn-approach-text" /></div>
          <div className="sm:col-span-2"><ListEditor label="Feature Cards" items={rn.features} onChange={(v) => setRnK("features", v)} prefix="rn-feature" fields={[{ key: "title", label: "Title" }, { key: "description", label: "Description", type: "textarea" }]} /></div>
          <div className="rounded-lg border border-slate-700 p-4 sm:col-span-2">
            <div className="mb-3 text-sm font-bold uppercase text-slate-200">Homepage Section</div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldText label="Home Heading" value={rn.home_heading} onChange={(v) => setRnK("home_heading", v)} testid="rn-home-heading" />
              <FieldText label="Home Button Text" value={rn.home_button} onChange={(v) => setRnK("home_button", v)} testid="rn-home-button" />
              <div className="sm:col-span-2"><FieldText label="Home Text" value={rn.home_text} onChange={(v) => setRnK("home_text", v)} textarea testid="rn-home-text" /></div>
              <div className="sm:col-span-2"><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Home Image</label><ImageUpload value={rn.home_image} onChange={(v) => setRnK("home_image", v)} testid="rn-home-image" /></div>
            </div>
          </div>
          <FieldText label="SEO Title" value={rn.seo_title} onChange={(v) => setRnK("seo_title", v)} testid="rn-seo-title" />
          <div className="sm:col-span-2"><FieldText label="SEO Description" value={rn.seo_description} onChange={(v) => setRnK("seo_description", v)} textarea testid="rn-seo-desc" /></div>
        </PlatformCard>

        {/* UNTAMED */}
        <PlatformCard pageKey="untamed" title="Untamed Sports Gear" c={ug} setC={setUg}>
          <FieldText label="Supplier Badge" value={ug.badge} onChange={(v) => setUgK("badge", v)} testid="ug-badge" />
          <FieldText label="Page Heading" value={ug.heading} onChange={(v) => setUgK("heading", v)} testid="ug-heading" />
          <FieldText label="Tagline" value={ug.tagline} onChange={(v) => setUgK("tagline", v)} testid="ug-tagline" />
          <FieldText label="Button Text" value={ug.button_text} onChange={(v) => setUgK("button_text", v)} testid="ug-button" />
          <div className="sm:col-span-2"><FieldText label="Introduction" value={ug.intro} onChange={(v) => setUgK("intro", v)} textarea rows={4} testid="ug-intro" /></div>
          <FieldText label="External Website URL" value={ug.external_url} onChange={(v) => setUgK("external_url", v)} testid="ug-url" />
          <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Logo</label><ImageUpload value={ug.logo_url} onChange={(v) => setUgK("logo_url", v)} testid="ug-logo" /></div>
          <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Featured / Hero Image</label><ImageUpload value={ug.featured_image} onChange={(v) => setUgK("featured_image", v)} testid="ug-featured" /></div>
          <div className="sm:col-span-2"><ListEditor label="Product Categories" items={ug.categories} onChange={(v) => setUgK("categories", v)} prefix="ug-cat" fields={[{ key: "title", label: "Title" }, { key: "description", label: "Description", type: "textarea" }, { key: "image", label: "Image", type: "image" }]} /></div>
          <div className="rounded-lg border border-slate-700 p-4 sm:col-span-2">
            <div className="mb-3 text-sm font-bold uppercase text-slate-200">Homepage Section</div>
            <div className="grid gap-4 sm:grid-cols-2">
              <FieldText label="Home Heading" value={ug.home_heading} onChange={(v) => setUgK("home_heading", v)} testid="ug-home-heading" />
              <FieldText label="Home Button Text" value={ug.home_button} onChange={(v) => setUgK("home_button", v)} testid="ug-home-button" />
              <div className="sm:col-span-2"><FieldText label="Home Text" value={ug.home_text} onChange={(v) => setUgK("home_text", v)} textarea testid="ug-home-text" /></div>
              <div className="sm:col-span-2"><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Home Image</label><ImageUpload value={ug.home_image} onChange={(v) => setUgK("home_image", v)} testid="ug-home-image" /></div>
            </div>
          </div>
          <FieldText label="SEO Title" value={ug.seo_title} onChange={(v) => setUgK("seo_title", v)} testid="ug-seo-title" />
          <div className="sm:col-span-2"><FieldText label="SEO Description" value={ug.seo_description} onChange={(v) => setUgK("seo_description", v)} textarea testid="ug-seo-desc" /></div>
        </PlatformCard>

        {/* PARTNERS STRIP */}
        <div className="rounded-xl border border-slate-700 bg-[#151F30] p-5">
          <h2 className="mb-4 font-heading text-lg font-extrabold uppercase text-white">Homepage Partners Strip</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center justify-between rounded-lg border border-slate-700 p-4">
              <div><div className="text-sm font-bold uppercase text-slate-200">Show strip</div><div className="text-xs text-slate-400">Compact logo band near sponsors</div></div>
              <Switch checked={!!ps.visible} onCheckedChange={(v) => setPs({ ...ps, visible: v })} data-testid="partners-visible" />
            </div>
            <FieldText label="Heading" value={ps.heading} onChange={(v) => setPs({ ...ps, heading: v })} testid="partners-heading" />
          </div>
          <div className="mt-4 space-y-4">
            {(ps.items || []).map((it, i) => (
              <div key={i} className="rounded-lg border border-slate-700 bg-[#0F1826] p-4" data-testid={`partners-item-${i}`}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <FieldText label="Name" value={it.name} onChange={(v) => setPsItem(i, "name", v)} testid={`partners-${i}-name`} />
                  <FieldText label="Tagline" value={it.tagline} onChange={(v) => setPsItem(i, "tagline", v)} testid={`partners-${i}-tagline`} />
                  <FieldText label="Link (internal path)" value={it.link} onChange={(v) => setPsItem(i, "link", v)} testid={`partners-${i}-link`} />
                  <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Logo</label><ImageUpload value={it.logo_url} onChange={(v) => setPsItem(i, "logo_url", v)} testid={`partners-${i}-logo`} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
